import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { academicService } from '../../services/academicService';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, Users, GraduationCap } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const AssignedSubjectsPage = () => {
  const { user } = useAuth();
  const [subjects, setSubjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch all subjects first
      const allSubjectsRes = await academicService.getSubjects();
      const allSubjects = allSubjectsRes.data?.data?.subjects || allSubjectsRes.data?.data || [];
      
      // Fetch classes to find which subjects are assigned to this staff member
      const classesRes = await academicService.getClasses({ teacherId: user?.id });
      const classes = classesRes.data?.data?.classes || classesRes.data?.data || [];
      
      // Extract unique subjects from assigned classes
      const assignedSubjectIds = new Set();
      classes.forEach(classItem => {
        const classSubjects = classItem.classSubjects || classItem.subjects || [];
        classSubjects.forEach(cs => {
          if (cs.teacher?.id === user?.id || cs.teacherId === user?.id) {
            assignedSubjectIds.add(cs.subject?.id || cs.subjectId);
          }
        });
      });
      
      // Filter subjects that are assigned to this staff
      const assignedSubjects = allSubjects.filter(s => assignedSubjectIds.has(s.id));
      setSubjects(assignedSubjects);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load assigned subjects');
      console.error('Error fetching subjects:', e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (user?.id) {
      fetchSubjects();
    }
  }, [user?.id]);

  const columns = [
    { 
      id: 'name', 
      header: 'Subject Name', 
      accessorKey: 'name', 
      sortable: true 
    },
    { 
      id: 'code', 
      header: 'Code', 
      accessorKey: 'code', 
      sortable: true 
    },
    { 
      id: 'description', 
      header: 'Description', 
      accessorKey: 'description' 
    },
    { 
      id: 'classes', 
      header: 'Classes', 
      accessorKey: 'classes',
      cell: ({ row }) => {
        if (!row?.original) return 'N/A';
        // This would need to be calculated from class-subject relationships
        return 'N/A';
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        if (!row?.original) return null;
        return (
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => window.location.assign(`/dashboard/academic/subjects/${row.original.id}`)}
          >
            View Details
          </Button>
        );
      }
    }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchSubjects}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">My Assigned Subjects</h2>
          <p className="text-gray-600">Subjects you are assigned to teach</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Subjects</p>
                <p className="text-2xl font-bold text-gray-900">{subjects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <GraduationCap className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Active Assignments</p>
                <p className="text-2xl font-bold text-gray-900">{subjects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {subjects.length === 0 ? (
        <EmptyState 
          icon={BookOpen}
          title="No assigned subjects"
          description="You are not currently assigned to teach any subjects."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <DataTable data={subjects} columns={columns} searchable />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AssignedSubjectsPage;

